import { useEffect, useState, useRef, useCallback } from "react";
import {
  GraduationCap, Loader2, History, RefreshCw, AlertTriangle, FileQuestion, FileText, Radio,
  Timer, ChevronRight, ArrowLeft, BookOpen, ClipboardCheck, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { formatApiErrorDetail } from "../context/AuthContext";
import { useExamMode } from "../context/ExamModeContext";
import { PenalCodeBrowser, RadioCodes } from "./AreaAgenti";
import { ExamAuthRequestPanel } from "../components/ExamAuthRequestPanel";
import ManualeOperatore from "../components/ManualeOperatore";
import FormazioneTimeline from "../components/FormazioneTimeline";

const EXAM_DURATION_MS = 15 * 60 * 1000; // 15 minuti
const EXAM_START_KEY = "lssd_exam_start_ts";

const VIEWS = {
  HOME: "home",
  EXAM: "exam",
  PENAL: "penal",
  RADIO: "radio",
  SOP: "sop",
  HISTORY: "history",
  FORMAZIONE: "formazione",
};

export default function AreaAllievi() {
  const [view, setView] = useState(VIEWS.HOME);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { setExamMode } = useExamMode();

  // Attiva la "exam mode" (nasconde header dashboard) solo quando siamo dentro l'esame
  useEffect(() => {
    setExamMode(view === VIEWS.EXAM);
    return () => setExamMode(false);
  }, [view, setExamMode]);

  const goHome = useCallback(() => setView(VIEWS.HOME), []);

  if (view === VIEWS.HOME) {
    return (
      <HomePanels
        onStartExam={() => setConfirmOpen(true)}
        onOpenPenal={() => setView(VIEWS.PENAL)}
        onOpenRadio={() => setView(VIEWS.RADIO)}
        onOpenSop={() => setView(VIEWS.SOP)}
        onOpenHistory={() => setView(VIEWS.HISTORY)}
        onOpenFormazione={() => setView(VIEWS.FORMAZIONE)}
        confirmOpen={confirmOpen}
        onCloseConfirm={() => setConfirmOpen(false)}
        onConfirmStart={() => { setConfirmOpen(false); setView(VIEWS.EXAM); }}
      />
    );
  }

  if (view === VIEWS.EXAM) return <ExamPanel onExit={goHome} />;
  if (view === VIEWS.PENAL) return <BackWrap onBack={goHome} title="Codice Penale" icon={FileText}><PenalCodeBrowser /></BackWrap>;
  if (view === VIEWS.RADIO) return <BackWrap onBack={goHome} title="Codici Radio" icon={Radio}><RadioCodes /></BackWrap>;
  if (view === VIEWS.SOP) return <BackWrap onBack={goHome} title="Manuale dell'Operatore" icon={BookOpen}><ManualeOperatore /></BackWrap>;
  if (view === VIEWS.FORMAZIONE) return <BackWrap onBack={goHome} title="Percorso Formativo" icon={Sparkles}><FormazioneTimeline /></BackWrap>;
  if (view === VIEWS.HISTORY) return <BackWrap onBack={goHome} title="Storico Tentativi" icon={History}><AttemptsHistory /></BackWrap>;
  return null;
}

function BackWrap({ onBack, title, icon: Icon, children }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          onClick={onBack}
          variant="outline"
          size="sm"
          data-testid="back-to-allievi-home"
          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
        </Button>
        <div className="flex items-center gap-2 text-sheriff-gold">
          <Icon className="h-5 w-5" />
          <span className="font-heading font-bold text-xl text-[#F5E6B8]">{title}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

// ============================================================
// HOME PANELS — 4 cards di scelta
// ============================================================
function HomePanels({ onStartExam, onOpenPenal, onOpenRadio, onOpenSop, onOpenHistory, onOpenFormazione, confirmOpen, onCloseConfirm, onConfirmStart }) {
  return (
    <div className="space-y-8" data-testid="area-allievi-home">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <GraduationCap className="h-4 w-4" /> Area Allievi
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-2">
          Percorso Allievo Vice Sceriffo
        </h1>
        <p className="text-[#D4C480] max-w-2xl">
          Benvenuto Allievo. Studia il materiale a tua disposizione e, quando ti senti pronto,
          sostieni l&apos;esame teorico per ottenere la promozione a <span className="text-sheriff-gold font-semibold">Vice Sceriffo</span>.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <PanelCard
          highlight
          icon={ClipboardCheck}
          title="Esame Teorico"
          subtitle="Vice Sceriffo"
          desc="Sostieni l'esame teorico. 15 minuti, 1 tentativo ogni 7 giorni. Allo scadere del tempo l'esame viene inviato automaticamente."
          ctaLabel="Inizia Esame"
          onClick={onStartExam}
          testId="panel-start-exam"
        />
        <PanelCard
          icon={Sparkles}
          title="Percorso Formativo"
          subtitle="La tua timeline"
          desc="Segui i passi del tuo iter di formazione: colloqui, corsi, prove pratiche, esame teorico e cerimonia finale. Aggiornato dalle Risorse Umane."
          ctaLabel="Apri Timeline"
          onClick={onOpenFormazione}
          testId="panel-formazione"
        />
        <PanelCard
          icon={BookOpen}
          title="Codice Penale"
          subtitle="Studio"
          desc="Consulta il Codice Penale completo: articoli, descrizioni e pene previste. Materiale di riferimento per l'esame."
          ctaLabel="Apri Codice"
          onClick={onOpenPenal}
          testId="panel-penal"
        />
        <PanelCard
          icon={Radio}
          title="Codici Radio"
          subtitle="Comunicazioni"
          desc="Tabella dei codici 10 e codici di stato utilizzati durante il servizio operativo. Memorizzali tutti."
          ctaLabel="Apri Codici"
          onClick={onOpenRadio}
          testId="panel-radio"
        />
        <PanelCard
          icon={BookOpen}
          title="Manuale Operatore"
          subtitle="Procedure Standard"
          desc="Manuale ufficiale del Dipartimento: storia LSSD, gerarchia dei gradi, divisioni operative e logistiche, e tutte le procedure standard."
          ctaLabel="Apri Manuale"
          onClick={onOpenSop}
          testId="panel-sop"
        />
        <PanelCard
          icon={History}
          title="Storico Tentativi"
          subtitle="I tuoi esami"
          desc="Visualizza i tuoi tentativi precedenti e lo stato di valutazione delle Risorse Umane."
          ctaLabel="Apri Storico"
          onClick={onOpenHistory}
          testId="panel-history"
        />
      </div>

      <StartConfirmDialog open={confirmOpen} onClose={onCloseConfirm} onConfirm={onConfirmStart} />
    </div>
  );
}

function PanelCard({ icon: Icon, title, subtitle, desc, ctaLabel, onClick, testId, highlight }) {
  const baseCls = "relative overflow-hidden rounded-2xl border p-6 flex flex-col group transition-all hover:-translate-y-0.5 hover:shadow-2xl";
  const cls = highlight
    ? `${baseCls} border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/20 via-[#143830] to-[#0F2620] hover:border-sheriff-gold shadow-lg shadow-sheriff-gold/10`
    : `${baseCls} border-sheriff-gold/25 bg-gradient-to-br from-[#143830] to-[#0F2620] hover:border-sheriff-gold/60`;
  return (
    <div data-testid={testId} className={cls}>
      {highlight && (
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-sheriff-gold/15 blur-3xl pointer-events-none" />
      )}
      <div className="relative flex items-start gap-3 mb-3">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border ${highlight ? "border-sheriff-gold/60 bg-sheriff-gold/20" : "border-sheriff-gold/30 bg-sheriff-gold/10"}`}>
          <Icon className="h-6 w-6 text-sheriff-gold" />
        </div>
        <div>
          <div className="uppercase-label text-sheriff-gold/80 text-[10px] mb-0.5">{subtitle}</div>
          <h3 className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter">{title}</h3>
        </div>
      </div>
      <p className="text-sm text-[#D4C480] mb-5 leading-relaxed relative">{desc}</p>
      <Button
        onClick={onClick}
        data-testid={`${testId}-cta`}
        className={`mt-auto rounded-full uppercase-label font-bold ${
          highlight
            ? "bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18]"
            : "bg-sheriff-gold/15 hover:bg-sheriff-gold/25 text-sheriff-gold border border-sheriff-gold/40"
        }`}
      >
        {ctaLabel} <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

function StartConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="exam-confirm-dialog">
        <DialogHeader>
          <div className="h-14 w-14 rounded-2xl bg-sheriff-gold/20 border border-sheriff-gold/50 flex items-center justify-center mb-2">
            <Sparkles className="h-7 w-7 text-sheriff-gold" />
          </div>
          <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter">
            Sei sicuro di voler iniziare?
          </DialogTitle>
          <DialogDescription className="text-[#D4C480] pt-2">
            Stai per iniziare l&apos;<span className="font-bold text-sheriff-gold">Esame Teorico per diventare Vice Sceriffo</span>.
          </DialogDescription>
          <div className="text-[#D4C480] pt-2 text-sm">
            Una volta avviato:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Avrai <span className="font-bold text-sheriff-gold">15 minuti</span> per completare l&apos;esame</li>
              <li>Il timer parte adesso e <span className="font-bold text-sheriff-gold">non si ferma</span></li>
              <li>Allo scadere del tempo, l&apos;esame verrà inviato automaticamente</li>
              <li>Potrai sostenere un altro tentativo solo dopo <span className="font-bold text-sheriff-gold">7 giorni</span></li>
            </ul>
          </div>
        </DialogHeader>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="border-sheriff-gold/40 text-sheriff-gold rounded-full uppercase-label" data-testid="exam-confirm-cancel">
            Annulla
          </Button>
          <Button onClick={onConfirm} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label" data-testid="exam-confirm-start">
            <Sparkles className="h-4 w-4 mr-1.5" /> Inizia Esame
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// EXAM PANEL — esame attivo con timer fixed
// ============================================================
// ============================================================
// HOOKS / SUB-COMPONENTS (estratti da ExamPanel)
// ============================================================
function useQuizTimer({ startedAt, durationMs, paused, onTimeout }) {
  const [timeLeftMs, setTimeLeftMs] = useState(durationMs);
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  useEffect(() => {
    if (!startedAt || paused) return undefined;
    const tick = () => {
      const remaining = Math.max(0, durationMs - (Date.now() - startedAt));
      setTimeLeftMs(remaining);
      if (remaining <= 0) onTimeoutRef.current?.();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, paused, durationMs]);

  return timeLeftMs;
}

function QuizQuestion({ q, index, total, answer, onPick, onText }) {
  const isOpen = q.type === "open";
  return (
    <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-5 sm:p-6" data-testid={`exam-question-${index}`}>
      <div className="mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60 mb-1 flex items-center gap-2 flex-wrap">
          <span>Domanda {String(index + 1).padStart(2, "0")} di {String(total).padStart(2, "0")}</span>
          {q.category && <span className="text-[#B8A269]">· {q.category}</span>}
          {isOpen && <span className="px-1.5 py-0.5 rounded bg-sheriff-gold/15 text-sheriff-gold text-[9px]">RISPOSTA APERTA</span>}
        </div>
        <h3 className="font-heading font-bold text-base sm:text-lg text-[#F5E6B8] leading-snug">{q.text}</h3>
      </div>
      {isOpen ? (
        <Textarea
          data-testid={`exam-text-${index}`}
          value={answer?.text_answer || ""}
          onChange={(e) => onText(q.id, e.target.value)}
          placeholder="Scrivi qui la tua risposta…"
          rows={4}
          className="bg-[#0F2620] border-sheriff-gold/30 rounded-xl text-[#F5E6B8] placeholder:text-[#8B7B49]"
        />
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {(q.options || []).map((opt, idx) => {
            const isPicked = answer?.selected_index === idx;
            const cls = isPicked
              ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
              : "border-sheriff-gold/20 hover:border-sheriff-gold/60 bg-[#0F2620] text-[#E8D89A]";
            return (
              <button
                key={`${q.id}-opt-${idx}`}
                data-testid={`exam-option-${index}-${idx}`}
                onClick={() => onPick(q.id, idx)}
                className={`text-left text-sm rounded-xl border px-4 py-3 transition-all ${cls} hover:-translate-y-0.5`}
              >
                <span className="font-mono font-bold mr-2">{String.fromCharCode(65 + idx)})</span>
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ExamPanel({ onExit }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [nextAttemptAt, setNextAttemptAt] = useState(null);
  const [cooldownDays, setCooldownDays] = useState(null);
  const [examStartedAt, setExamStartedAt] = useState(null);
  const [tierMeta, setTierMeta] = useState({ next_role: "Vice Sceriffo", exam_tier_label: "Vice Sceriffo", pass_threshold: 70 });
  const [sessionInfo, setSessionInfo] = useState(null);
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const loadQuiz = useCallback(async () => {
    try {
      const { data } = await api.get("/quiz/questions");
      setSessionInfo(data);
      setQuestions(data.questions || []);
      setRateLimited(!!data.rate_limited);
      setNextAttemptAt(data.next_attempt_at || null);
      if (data.cooldown_days) setCooldownDays(data.cooldown_days);
      setTierMeta({
        next_role: data.next_role || "Vice Sceriffo",
        exam_tier_label: data.exam_tier_label || "Vice Sceriffo",
        pass_threshold: data.pass_threshold || 70,
      });
      if (!data.rate_limited && (data.questions || []).length > 0) {
        const stored = parseInt(sessionStorage.getItem(EXAM_START_KEY) || "", 10);
        const startTs = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
        if (!Number.isFinite(stored) || stored <= 0) {
          sessionStorage.setItem(EXAM_START_KEY, startTs.toString());
        }
        setExamStartedAt(startTs);
      } else {
        setExamStartedAt(null);
        sessionStorage.removeItem(EXAM_START_KEY);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore caricamento esame");
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica esame al mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadQuiz();
    })();
    return () => { cancelled = true; };
  }, [loadQuiz]);

  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length && questions.length > 0;

  const handlePick = useCallback((qid, idx) => {
    setAnswers((prev) => ({ ...prev, [qid]: { selected_index: idx } }));
  }, []);
  const handleText = useCallback((qid, text) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (text.trim()) next[qid] = { text_answer: text };
      else delete next[qid];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async (forced = false) => {
    if (!forced && !allAnswered) {
      toast.error("Rispondi a tutte le domande prima di inviare");
      return;
    }
    setSubmitting(true);
    try {
      const currentAnswers = answersRef.current;
      const payload = {
        answers: Object.entries(currentAnswers).map(([question_id, val]) => ({
          question_id,
          selected_index: val.selected_index ?? null,
          text_answer: val.text_answer ?? null,
        })),
      };
      const { data } = await api.post("/quiz/submit", payload);
      setResult(data);
      sessionStorage.removeItem(EXAM_START_KEY);
      setExamStartedAt(null);
      if (forced) toast("Tempo scaduto. Esame inviato automaticamente.");
      else toast.success("Esame inviato. In attesa di valutazione.");
      setRateLimited(true);
      // Recupera il next_attempt_at corretto dal server (rispetta il cooldown del tier)
      try {
        const { data: refreshed } = await api.get("/quiz/questions");
        setNextAttemptAt(refreshed.next_attempt_at || null);
        if (refreshed.cooldown_days) setCooldownDays(refreshed.cooldown_days);
      } catch {
        // Fallback in caso di rete down: ricalcola lato client usando il cooldown noto del tier
        const fallbackDays = cooldownDays || (tierMeta.next_role === "Vice Sceriffo" ? 7 : 5);
        const next = new Date(Date.now() + fallbackDays * 24 * 60 * 60 * 1000).toISOString();
        setNextAttemptAt(next);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail);
      if (e?.response?.status === 429) {
        toast.error(detail || "Puoi sostenere l'esame solo una volta a settimana");
        setRateLimited(true);
        sessionStorage.removeItem(EXAM_START_KEY);
        // Ricarica stato server (nextAttemptAt aggiornato)
        try {
          const { data: refreshed } = await api.get("/quiz/questions");
          setNextAttemptAt(refreshed.next_attempt_at || null);
        } catch {/* lascia stato precedente */}
      } else {
        toast.error(detail || "Errore invio esame");
      }
    } finally {
      setSubmitting(false);
    }
  }, [allAnswered]);

  // Timer countdown (hook estratto)
  const timeLeftMs = useQuizTimer({
    startedAt: examStartedAt,
    durationMs: EXAM_DURATION_MS,
    paused: rateLimited || !!result || submitting,
    onTimeout: () => handleSubmit(true),
  });

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" />
      </div>
    );
  }

  if (result) return <NeutralResult onBack={onExit} nextAttemptAt={nextAttemptAt} cooldownDays={cooldownDays} />;

  if (rateLimited) {
    const block = sessionInfo?.block_reason;
    if (block === "auth_required" || block === "session_closed") {
      return (
        <div className="space-y-6" data-testid="exam-blocked-auth">
          <BackBtn onBack={onExit} />
          <ExamAuthRequestPanel
            sessionInfo={sessionInfo}
            tierLabel={tierMeta.exam_tier_label}
            nextRole={tierMeta.next_role}
            onRefresh={loadQuiz}
          />
          <MeantimeCards onBack={onExit} />
        </div>
      );
    }
    return (
      <div className="space-y-6" data-testid="exam-blocked">
        <BackBtn onBack={onExit} />
        <RateLimitBlock nextAttemptAt={nextAttemptAt} cooldownDays={cooldownDays} />
        <MeantimeCards onBack={onExit} />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="space-y-6">
        <BackBtn onBack={onExit} />
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-10 text-center">
          <FileQuestion className="h-12 w-12 text-sheriff-gold/50 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl text-[#F5E6B8] mb-2">Nessuna domanda configurata</h3>
          <p className="text-[#B8A269]">Il Comando non ha ancora pubblicato l&apos;esame teorico. Riprova più tardi.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24" data-testid="exam-active">
      <FixedExamTimer timeLeftMs={timeLeftMs} totalMs={EXAM_DURATION_MS} />

      <div className="pt-2">
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" /> Esame in corso
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-2">
          Esame Teorico — {tierMeta.next_role}
        </h1>
      </div>

      <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/30 rounded-2xl p-5 flex items-start gap-4">
        <AlertTriangle className="h-5 w-5 text-sheriff-gold mt-0.5 shrink-0" />
        <div className="text-sm text-[#E8D89A]">
          Rispondi a tutte le <span className="font-bold text-sheriff-gold">{questions.length} domande</span> entro
          il tempo limite di <span className="font-bold text-sheriff-gold">15 minuti</span>.
          Soglia di superamento: <span className="font-bold text-sheriff-gold">{tierMeta.pass_threshold}%</span>.
          Allo scadere del tempo, l&apos;esame verrà inviato automaticamente con le risposte fornite.
        </div>
      </div>

      <div className="space-y-4">
        {questions.map((q, i) => (
          <QuizQuestion
            key={q.id}
            q={q}
            index={i}
            total={questions.length}
            answer={answers[q.id]}
            onPick={handlePick}
            onText={handleText}
          />
        ))}
      </div>

      <div className="bg-gradient-to-r from-[#143830] to-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sticky bottom-4 backdrop-blur-sm z-30">
        <div className="text-sm">
          <span className="font-mono text-sheriff-gold font-bold">{answeredCount}</span>
          <span className="text-[#B8A269]"> / {questions.length} risposte date</span>
        </div>
        <Button
          onClick={() => handleSubmit(false)}
          disabled={!allAnswered || submitting}
          data-testid="exam-submit"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-[0.12em] rounded-full px-6"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
          Invia Esame
        </Button>
      </div>
    </div>
  );
}

function BackBtn({ onBack }) {
  return (
    <Button onClick={onBack} variant="outline" size="sm" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label" data-testid="back-to-allievi-home">
      <ArrowLeft className="h-4 w-4 mr-1" /> Torna ai Pannelli
    </Button>
  );
}

// ============================================================
// FIXED EXAM TIMER — sempre visibile in alto durante l'esame
// ============================================================
function FixedExamTimer({ timeLeftMs, totalMs }) {
  const totalSeconds = Math.ceil(timeLeftMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const pct = Math.max(0, Math.min(100, (timeLeftMs / totalMs) * 100));
  const isCritical = timeLeftMs <= 2 * 60 * 1000;
  const isLow = timeLeftMs <= 5 * 60 * 1000;

  const bgClass = isCritical
    ? "border-rose-500/60 bg-gradient-to-r from-rose-900/70 to-[#0F2620]/95"
    : isLow
      ? "border-amber-500/50 bg-gradient-to-r from-amber-900/60 to-[#0F2620]/95"
      : "border-sheriff-gold/40 bg-gradient-to-r from-[#143830]/95 to-[#0F2620]/95";
  const textClass = isCritical ? "text-rose-300" : isLow ? "text-amber-300" : "text-sheriff-gold";
  const barClass = isCritical ? "bg-rose-500" : isLow ? "bg-amber-500" : "bg-sheriff-gold";

  return (
    <div
      data-testid="exam-timer"
      className={`sticky top-0 z-40 -mx-2 sm:mx-0 border-2 rounded-2xl ${bgClass} backdrop-blur-xl shadow-2xl shadow-black/60`}
    >
      <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 border ${isCritical ? "border-rose-400/50 bg-rose-500/15 animate-pulse" : isLow ? "border-amber-400/50 bg-amber-500/15" : "border-sheriff-gold/40 bg-sheriff-gold/10"}`}>
          <Timer className={`h-5 w-5 ${textClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`uppercase tracking-[0.18em] text-[10px] font-bold ${textClass} mb-0.5`}>
            {isCritical ? "Tempo Quasi Scaduto!" : "Tempo Rimanente"}
          </div>
          <div className={`font-mono font-black text-2xl sm:text-3xl tracking-tighter ${textClass}`} data-testid="exam-timer-value">
            {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
          </div>
        </div>
      </div>
      <div className="h-1 bg-[#0A1F18] rounded-b-2xl overflow-hidden">
        <div className={`h-full ${barClass} transition-all duration-1000 ease-linear`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ============================================================
// NEUTRAL RESULT — non comunica esito all'allievo
// ============================================================
function NeutralResult({ onBack, nextAttemptAt, cooldownDays }) {
  const days = cooldownDays || 7;
  return (
    <div className="space-y-6" data-testid="exam-completed">
      <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border-2 border-sheriff-gold/40 rounded-2xl p-8 sm:p-10 text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sheriff-gold/15 border border-sheriff-gold/40 mb-4">
          <ClipboardCheck className="h-8 w-8 text-sheriff-gold" />
        </div>
        <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#F5E6B8] tracking-tighter mb-3">
          Esame Completato
        </h2>
        <p className="text-[#D4C480] max-w-xl mx-auto mb-6 leading-relaxed">
          Le tue risposte sono state inviate alle <span className="font-bold text-sheriff-gold">Risorse Umane</span> per la valutazione.
          Riceverai comunicazione sull&apos;esito attraverso i canali ufficiali del Dipartimento.
          <br /><br />
          Potrai sostenere un altro tentativo trascorsi <span className="font-bold text-sheriff-gold">{days} giorni</span>.
        </p>
        <NextAttemptCountdown nextAttemptAt={nextAttemptAt} />
        <div className="mt-6">
          <Button
            onClick={onBack}
            variant="outline"
            data-testid="exam-back-home"
            className="border-sheriff-gold/50 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Torna ai Pannelli
          </Button>
        </div>
      </div>
      <MeantimeCards onBack={onBack} />
    </div>
  );
}

// ============================================================
// MEANTIME CARDS — sblocca l'utente offrendo attività alternative
// (usato dopo submit / durante pending / con cooldown attivo)
// ============================================================
function MeantimeCards({ onBack }) {
  return (
    <div data-testid="meantime-cards" className="space-y-3">
      <div className="uppercase-label text-sheriff-gold/80 text-[10px] flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> Nel frattempo puoi
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <MeantimeCard
          icon={BookOpen}
          title="Studia il Manuale"
          desc="Ripassa le nozioni fondamentali dell'operatore LSSD."
          onClick={onBack}
          testId="meantime-manuale"
        />
        <MeantimeCard
          icon={FileText}
          title="Codice Penale"
          desc="Consulta il Codice Penale di San Andreas."
          onClick={onBack}
          testId="meantime-penal"
        />
        <MeantimeCard
          icon={Sparkles}
          title="Percorso Formativo"
          desc="Verifica gli step di formazione completati."
          onClick={onBack}
          testId="meantime-formazione"
        />
      </div>
      <div className="pt-1">
        <Button
          onClick={onBack}
          variant="outline"
          size="sm"
          data-testid="meantime-back-home"
          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Torna ai Pannelli
        </Button>
      </div>
    </div>
  );
}

function MeantimeCard({ icon: Icon, title, desc, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-left bg-[#0F2620] hover:bg-[#143830] border border-sheriff-gold/25 hover:border-sheriff-gold/60 rounded-xl p-4 transition-all group"
    >
      <div className="flex items-center gap-2 mb-1.5 text-sheriff-gold">
        <Icon className="h-4 w-4" />
        <span className="font-heading font-bold text-sm text-[#F5E6B8] group-hover:text-sheriff-gold transition-colors">{title}</span>
      </div>
      <p className="text-xs text-[#B8A269] leading-relaxed">{desc}</p>
    </button>
  );
}

function RateLimitBlock({ nextAttemptAt, cooldownDays }) {
  const days = cooldownDays || 7;
  return (
    <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border-2 border-sheriff-gold/40 rounded-2xl p-8 sm:p-10 text-center" data-testid="exam-rate-limited">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sheriff-gold/15 border border-sheriff-gold/40 mb-4">
        <AlertTriangle className="h-8 w-8 text-sheriff-gold" />
      </div>
      <h3 className="font-heading font-black text-2xl sm:text-3xl text-[#F5E6B8] mb-3 tracking-tighter">
        Hai già sostenuto l&apos;esame di recente
      </h3>
      <p className="text-[#D4C480] max-w-xl mx-auto mb-5">
        Devi attendere <span className="font-bold text-sheriff-gold">{days} giorni</span> dal tuo ultimo tentativo prima di poter ritentare.
        Preparati meglio per il prossimo esame e ritenta quando il timer arriva a zero.
      </p>
      <NextAttemptCountdown nextAttemptAt={nextAttemptAt} />
    </div>
  );
}

function NextAttemptCountdown({ nextAttemptAt }) {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    if (!nextAttemptAt) return;
    const tick = () => {
      const ms = new Date(nextAttemptAt).getTime() - Date.now();
      if (ms <= 0) { setRemaining("disponibile ora"); return; }
      const d = Math.floor(ms / 86_400_000);
      const h = Math.floor((ms % 86_400_000) / 3_600_000);
      const m = Math.floor((ms % 3_600_000) / 60_000);
      const s = Math.floor((ms % 60_000) / 1000);
      if (d > 0) {
        setRemaining(`${d}g ${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`);
      } else {
        setRemaining(`${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextAttemptAt]);
  return (
    <div className="inline-flex items-center gap-3 bg-[#0A1F18] border border-sheriff-gold/30 rounded-full px-5 py-3" data-testid="exam-countdown">
      <RefreshCw className="h-4 w-4 text-sheriff-gold" />
      <div className="text-left">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold/70">Prossimo tentativo</div>
        <div className="font-mono font-bold text-sheriff-gold">{remaining || "—"}</div>
      </div>
    </div>
  );
}

// ============================================================
// ATTEMPTS HISTORY — stato neutro (in attesa di valutazione)
// ============================================================
function AttemptsHistory() {
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    api.get("/quiz/my-attempts")
      .then(({ data }) => setAttempts(data))
      .catch(() => toast.error("Errore caricamento storico"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;

  if (attempts.length === 0) {
    return (
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-10 text-center">
        <History className="h-10 w-10 text-sheriff-gold/50 mx-auto mb-3" />
        <p className="text-[#B8A269]">Nessun tentativo registrato.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl divide-y divide-sheriff-gold/15 overflow-hidden">
      {attempts.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-4 px-5 py-4">
          <div>
            <div className="font-mono text-xs text-sheriff-gold">
              {new Date(a.submitted_at).toLocaleString("it-IT")}
            </div>
            <div className="text-sm text-[#E8D89A] mt-0.5">
              Esame consegnato
            </div>
          </div>
          <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold border bg-sheriff-gold/10 text-sheriff-gold border-sheriff-gold/40">
            In valutazione HR
          </span>
        </div>
      ))}
    </div>
  );
}
